// V1 settings skeleton — tag + team CRUD; M3 SPEC.md Faz 6 team-management
// ceremony backend ratify.
//
// TagManagement — Volkan direction 2026-05-23:
//   "TAG olsun. Tagler kullanıcının kendisi tarafından settingslerden
//    ayarlanabilsin"
//
// Mock-only CRUD against MSW (/settings/tags). Backend binding M3 Stage 4
// SPEC.md Faz 4 (Cross-cutting policy + reference taxonomy).
// Cross-link: tags appear in Order Management filter/batch rail; scope
// controls which surfaces can attach a given tag.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import {
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog'
import { Dialog } from '../../components/ui/dialog.lib'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { formatDate } from '../../lib/datetime'
import { useT } from '../../i18n'
import { tagManagementI18n } from '../../i18n/settings/tag-management'
import {
  TAG_COLORS,
  TAG_COLOR_CLASSES,
  type TagColor,
  type TagScope,
  type TagSetting,
} from '../../lib/tag-colors'
import { RealTagManagement } from './RealTagManagement'
import type { SettingsSurface } from './Settings'
import { DataTableShell } from '../../components/ui/data-table-shell'

type TagListResponse = {
  data: TagSetting[]
  meta: { total: number }
}

type FormState = {
  label: string
  color: TagColor
  scope: TagScope
}

const SCOPE_VALUES: readonly TagScope[] = ['all', 'admin', 'seller', 'supply'] as const

function emptyForm(): FormState {
  return { label: '', color: 'sky', scope: 'all' }
}

export function TagManagement({ surface }: { surface: SettingsSurface }) {
  if (USE_REAL_API) return <RealTagManagement surface={surface} />
  return <MaketTagManagement surface={surface} />
}

function MaketTagManagement({ surface }: { surface: SettingsSurface }) {
  const t = useT(tagManagementI18n)
  const queryClient = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<TagSetting | null>(null)
  const [form, setForm] = useState<FormState>(emptyForm())
  const [deleteTarget, setDeleteTarget] = useState<TagSetting | null>(null)
  const [error, setError] = useState<string | null>(null)

  const scopeParam: TagScope = surface === 'admin' ? 'all' : surface
  const queryKey = ['settings', 'tags', surface]

  const { data, isLoading, isError } = useQuery<TagListResponse>({
    queryKey,
    queryFn: async () => {
      // Admin surface intentionally requests every record; we then group on
      // the client. Non-admin surfaces let the MSW handler narrow to scope
      // matches (own surface + 'all').
      const url =
        surface === 'admin'
          ? apiUrl('/settings/tags')
          : apiUrl(`/settings/tags?scope=${scopeParam}`)
      const res = await fetch(url)
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
  })

  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['settings', 'tags'] })

  const saveMutation = useMutation({
    mutationFn: async (payload: { id?: string; body: FormState }) => {
      const method = payload.id ? 'PATCH' : 'POST'
      const url = payload.id
        ? apiUrl(`/settings/tags/${payload.id}`)
        : apiUrl('/settings/tags')
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload.body),
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          detail?: string
        }
        throw new Error(body.detail ?? `HTTP ${res.status}`)
      }
      return (await res.json()) as TagSetting
    },
    onSuccess: () => {
      refresh()
      setModalOpen(false)
      setEditing(null)
      setError(null)
    },
    onError: (e: Error) => setError(e.message),
  })

  const deleteMutation = useMutation({
    mutationFn: async (payload: { id: string; force: boolean }) => {
      const qs = payload.force ? '?force=1' : ''
      const res = await fetch(apiUrl(`/settings/tags/${payload.id}${qs}`), {
        method: 'DELETE',
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
          detail?: string
          usage_count?: number
        }
        const err = new Error(body.detail ?? `HTTP ${res.status}`) as Error & {
          usage_count?: number
        }
        err.usage_count = body.usage_count
        throw err
      }
      return await res.json()
    },
    onSuccess: () => {
      refresh()
      setDeleteTarget(null)
      setError(null)
    },
  })

  const resetMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(apiUrl('/settings/tags/reset'), {
        method: 'POST',
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    onSuccess: () => refresh(),
  })

  const openCreate = () => {
    setEditing(null)
    setForm({
      ...emptyForm(),
      scope: surface === 'admin' ? 'all' : surface,
    })
    setError(null)
    setModalOpen(true)
  }

  const openEdit = (tag: TagSetting) => {
    setEditing(tag)
    setForm({ label: tag.label, color: tag.color, scope: tag.scope })
    setError(null)
    setModalOpen(true)
  }

  const submitForm = () => {
    if (!form.label.trim()) {
      setError(t.modal.labelRequired)
      return
    }
    saveMutation.mutate({ id: editing?.id, body: form })
  }

  const tags = data?.data ?? []

  // Surface gate (audit fix 2026-05-23): non-admin surfaces can only mutate
  // tags whose scope === own surface. 'all' scope = platform-level (admin-
  // managed). Display the tag, but hide Edit/Delete affordance for off-scope
  // entries. M3 backend iam-auth enforces the same rule server-side.
  const canManageTag = (tag: TagSetting) =>
    surface === 'admin' || tag.scope === surface

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{t.pageTitle}</h2>
          <p className="mt-1 text-sm text-slate-600">{t.pageDescription}</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => resetMutation.mutate()}
            disabled={resetMutation.isPending}
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
            {t.resetToDefaults}
          </Button>
          <Button variant="primary" size="sm" onClick={openCreate}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            {t.addTag}
          </Button>
        </div>
      </div>

      <div className="mt-5 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden">
        {isLoading && (
          <div className="p-6 text-sm text-slate-500">{t.states.loading}</div>
        )}
        {isError && (
          <div className="p-6 text-sm text-rose-700">{t.states.loadFailed}</div>
        )}
        {!isLoading && !isError && tags.length === 0 && (
          <div className="p-6 text-sm text-slate-500">{t.states.empty}</div>
        )}
        {!isLoading && !isError && tags.length > 0 && (
          <DataTableShell variant="embedded">
          <table>
            <thead>
              <tr>
                <th className="w-12">{t.columns.color}</th>
                <th>{t.columns.label}</th>
                <th>{t.columns.scope}</th>
                <th className="text-right">{t.columns.usage}</th>
                <th>{t.columns.created}</th>
                <th className="w-24 text-right">{t.columns.actions}</th>
              </tr>
            </thead>
            <tbody>
              {tags.map((tag) => {
                const c = TAG_COLOR_CLASSES[tag.color]
                const scopeLabel = t.scopeOptions[tag.scope].label
                return (
                  <tr key={tag.id}>
                    <td className="py-2">
                      <span
                        className={`inline-block h-4 w-4 rounded-full ring-2 ring-offset-1 ${c.ring} ${c.swatch}`}
                      />
                    </td>
                    <td className="py-2">
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ${c.chip}`}
                      >
                        {tag.label}
                      </span>
                    </td>
                    <td className="py-2">
                      <Badge variant="default">{scopeLabel}</Badge>
                    </td>
                    <td className="py-2 text-right tabular-nums text-[var(--color-text)]">
                      {tag.usage_count}
                    </td>
                    <td className="py-2 text-xs text-[var(--color-text-muted)]">
                      {formatDate(tag.created_at, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                      <div className="text-[var(--color-text-muted)]">{tag.created_by}</div>
                    </td>
                    <td className="py-2 text-right">
                      {canManageTag(tag) ? (
                        <div className="inline-flex gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openEdit(tag)}
                            aria-label={t.ariaLabels.editTag(tag.label)}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteTarget(tag)}
                            aria-label={t.ariaLabels.deleteTag(tag.label)}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-[var(--color-danger)]" />
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-[var(--color-text-muted)]">—</span>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </DataTableShell>
        )}
      </div>

      <p className="mt-3 text-xs text-slate-400">{t.footerNote}</p>

      {/* Add/Edit modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? t.modal.editTitle : t.modal.addTitle}</DialogTitle>
            <DialogDescription>{t.modal.description}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                {t.modal.labelField}
              </label>
              <input
                type="text"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-300"
                placeholder={t.modal.labelPlaceholder}
                maxLength={48}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1.5">
                {t.modal.colorField}
              </label>
              <div className="grid grid-cols-10 gap-2">
                {TAG_COLORS.map((c) => {
                  const cls = TAG_COLOR_CLASSES[c]
                  const isActive = form.color === c
                  return (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, color: c }))}
                      className={
                        `h-7 w-7 rounded-full ${cls.swatch} transition-all ` +
                        (isActive
                          ? 'ring-2 ring-offset-2 ring-slate-900 scale-110'
                          : 'hover:scale-110')
                      }
                      aria-label={t.ariaLabels.colorSwatch(c)}
                      title={c}
                    />
                  )
                })}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                {t.modal.scopeField}
              </label>
              <select
                value={form.scope}
                onChange={(e) =>
                  setForm((f) => ({ ...f, scope: e.target.value as TagScope }))
                }
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                disabled={surface !== 'admin'}
              >
                {SCOPE_VALUES.map((value) => (
                  <option key={value} value={value}>
                    {t.scopeOptions[value].label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-slate-500">
                {t.scopeOptions[form.scope].hint}
              </p>
              {surface !== 'admin' && (
                <p className="mt-1 text-xs text-slate-400">
                  {t.modal.scopeAdminOnly}
                </p>
              )}
            </div>

            {error && (
              <div className="rounded-md border-l-4 border-l-rose-400 bg-rose-50 px-3 py-2 text-xs text-rose-800">
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setModalOpen(false)}
              disabled={saveMutation.isPending}
            >
              {t.modal.cancel}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={submitForm}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? t.modal.saving : t.modal.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={deleteTarget !== null}
        onOpenChange={(o) => !o && setDeleteTarget(null)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.deleteDialog.title}</DialogTitle>
            <DialogDescription>
              {t.deleteDialog.leadBefore}
              <strong className="text-slate-900">{deleteTarget?.label}</strong>
              {t.deleteDialog.leadAfter}
              {deleteTarget && deleteTarget.usage_count > 0 && (
                <span className="block mt-2 text-amber-800">
                  {t.deleteDialog.usageWarning(deleteTarget.usage_count)}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          {deleteMutation.error && (
            <div className="rounded-md border-l-4 border-l-rose-400 bg-rose-50 px-3 py-2 text-xs text-rose-800">
              {(deleteMutation.error as Error).message}
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDeleteTarget(null)}
              disabled={deleteMutation.isPending}
            >
              {t.deleteDialog.cancel}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={deleteMutation.isPending}
              onClick={() => {
                if (!deleteTarget) return
                deleteMutation.mutate({
                  id: deleteTarget.id,
                  force: deleteTarget.usage_count > 0,
                })
              }}
            >
              {deleteMutation.isPending ? t.deleteDialog.deleting : t.deleteDialog.confirm}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
